import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { connect } from "react-redux";
import { APIProvider } from '@vis.gl/react-google-maps';
import getDistance from 'geolib/es/getDistance';
import GoogleMap from './GoogleMap/GoogleMap';
import { GoogleMapAutocomplete } from './GoogleMap/GoogleMapAutocomplete';
import GoogleMapHandler from './GoogleMap/GoogleMapHandler';
import DealerListDetailTags from '../Components/GoogleMap/DealerListDetailTags';
import CloseDealerListDetailTags from '../Components/GoogleMap/CloseDealerListDetailTags';
import { googleAnalytics } from '../Services/googleAnalytics';
import { translate } from '../Services/translation';
import { loadDealerLocator, update } from '../Actions/ResellerMapList.action';

export const scrollToMap = () => {
    if(document.getElementById("google-map")){
        let toScroll = document.getElementById("google-map")
        toScroll.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest'
        });
    };
};

function DealerLocationContainer(props) {
    const [activeTab, setActiveTab] = useState(1);
    const selectedPlace = useSelector((state) => state.resellermaplist.autocompletePlace);
    const isMounted = useRef(false);
    const tabRefs = useRef([]);
    const labels = ['tab1', 'tab2', 'tab3']; // motsvarande tabb-ID:n

    const getCurrentLocation = useCallback(() => {
      navigator?.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
        props.update({ lat: latitude, longt: longitude });
      });

      googleAnalytics({
        event: 'dealerLocatorSearch',
        localization: 'getPosition',
      });

      scrollToMap();
    }, [props]);


    useEffect(() => {
      const skipLink = document.getElementById('skip-link');
      let hasTabbed = false;

      const handleFirstTab = (e) => {
        if (e.key === 'Tab' && !hasTabbed) {
          if (skipLink) {
            skipLink.classList.add('active');
            requestAnimationFrame(() => {
              skipLink.focus();
            });
          }
          hasTabbed = true;
          window.removeEventListener('keydown', handleFirstTab);
        }
      };

      const handleFocus = () => {
        skipLink?.classList.add('active');
      };

      const handleBlur = () => {
        skipLink?.classList.remove('active');
      };

      window.addEventListener('keydown', handleFirstTab);
      skipLink?.addEventListener('focus', handleFocus);
      skipLink?.addEventListener('blur', handleBlur);

      return () => {
        window.removeEventListener('keydown', handleFirstTab);
        skipLink?.removeEventListener('focus', handleFocus);
        skipLink?.removeEventListener('blur', handleBlur);
      };
    }, []);


    const updateMyLatitude = useCallback(() => {
      navigator?.geolocation.getCurrentPosition(({ coords: { latitude, longitude } }) => {
        props.update({ myLat: latitude, myLongt: longitude });
      });
    }, [props]);
  
    const getAssortmentsCount = useCallback(() => {
      let open_trailerscount = 0;
      let boat_trailerscount = 0;
      let servicecount = 0;
      let activecount = 0;

      const dealers = props.initialAllReseller;
  
      dealers.forEach(dealer => {
        dealer.productAssortment?.forEach(assortment => {
          if (assortment === 'open_trailers') open_trailerscount++;
          else if (assortment === 'boat_trailers') boat_trailerscount++;
          else if (assortment === 'service') servicecount++;
          else if (assortment === 'active' && props.isClickAndCollect) activecount++;
        });
      });
  
      props.update({ open_trailerscount, boat_trailerscount, servicecount, activecount });
    }, [props]);


    const handleArrowNavigation = (e, index) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = (index + 1) % 3;
        tabRefs.current[nextIndex]?.focus();
        document.getElementById(`tab${nextIndex + 1}`)?.click();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = (index - 1 + 3) % 3;
        tabRefs.current[prevIndex]?.focus();
        document.getElementById(`tab${prevIndex + 1}`)?.click();
      }
    };
  
    const onChange = useCallback((id) => {
        const selected = [...props.selected];
        const index = selected.indexOf(id);
  
        if (index > -1) {
          selected.splice(index, 1);
        }
        else {
          selected.push(id);
          googleAnalytics({
            event: 'dealerLocatorFilter',
            dealerLocatorFilter: id,
          });
        }
  
        props.update({ selected });
  
        const items = selected.length > 0
          ? props.initialItems.filter(dealer =>
              dealer.productAssortment && selected.every(item => dealer.productAssortment.includes(item))
            )
          : props.initialItems;
  
        const allReseller = selected.length > 0
          ? props.initialAllReseller.filter(dealer =>
              dealer.productAssortment && selected.every(item => dealer.productAssortment.includes(item))
            )
          : props.initialAllReseller;
  
        props.update({ items, allReseller });
        getAssortmentsCount();
      },
      [props, getAssortmentsCount]
    );
  
    const getResellerCount = useCallback(() => {
      return props.items.reduce((count, dealer) => count + dealer.resellerCount, 0);
    }, [props.items]);
  
    const getTotalClosestLocation = useCallback(() => {
      const { lat, longt, myLat, myLongt, firstload } = props;
      const currentLat = firstload && myLat !== 0 ? myLat : lat;
      const currentLongt = firstload && myLongt !== 0 ? myLongt : longt;
  
      let closestcount = 0;
  
      props.items.forEach(dealer => {
        dealer.resellerAddress.forEach(address => {
          const dist = getDistance(
            { latitude: currentLat, longitude: currentLongt },
            { latitude: address.latitude, longitude: address.longitude }
          );
          if (dist / 1000 <= 150) {
            closestcount++;
          }
        });
      });
  
      return closestcount;
    }, [props]);
  
    const setTab = useCallback((tab, tabName) => {
      setActiveTab(tab);
    
      googleAnalytics({
        event: 'dealerLocatorTab',
        dealerLocatorTab: tabName,
      });
    }, []);
  
    useEffect(() => {
      if (!isMounted.current) {
        props.getAPIData();
        updateMyLatitude();
        isMounted.current = true;
      }
      else if (props.firstload) {
        getCurrentLocation();
        getAssortmentsCount();
      }
    }, [props.firstload, getCurrentLocation, getAssortmentsCount, updateMyLatitude]);

    return (
      isMounted && (
            <APIProvider apiKey={'AIzaSyBasMn4C1xOwCE_YL6nr0pjQGYsT5A3a9A'}>
              <div className="map-header">
                  <h2 className="text-center">{translate('dealer.findyourdealer')}</h2>
                  <div className="map-header__wrapper">
                      <div className="row smaller-custom-width">
                          <div className="columns small-12 medium-12 large-3 button-wrap-search">
                              <button className="button" onClick={getCurrentLocation}>
                                  {translate('dealerlocator.getcurrentlocation')}
                              </button>
                          </div>
                          <div className="columns small-12 medium-12 large-8 map-header__search">
                          <GoogleMapAutocomplete props={props} update={props.update} restrictedcountry={props.restrictedcountry} />
                          </div>
                      </div>
                      <div className="row custom-width">
                          <div className="columns small-12 map-header__checkboxes">
                              <ul>
                                  <li className="custom-checkbox">
                                    <input
                                      type="checkbox"
                                      id='open_trailers'
                                      onChange={() => onChange("open_trailers")}
                                      checked={props.selected.includes("open_trailers")}
                                      onKeyDown={(e) => {
                                        if (e.key === ' ' || e.key === 'Enter') {
                                          e.preventDefault();
                                          onChange("open_trailers");
                                        }
                                      }}
                                    /> 
                                      <label htmlFor='open_trailers'>
                                        {translate('dealerlocator.checkboxtrailer')}
                                        ({props.open_trailerscount})
                                      </label>
                                      <i className="cart__icon" aria-hidden="true"></i>
                                  </li>
                                  <li className="custom-checkbox">
                                    <input 
                                      type="checkbox"
                                      id='boat_trailers'
                                      onChange={() => onChange("boat_trailers")}
                                      checked={props.selected.includes("boat_trailers")}
                                      onKeyDown={(e) => {
                                        if (e.key === ' ' || e.key === 'Enter') {
                                          e.preventDefault();
                                          onChange("boat_trailers");
                                        }
                                      }}
                                    />  
                                    <label htmlFor='boat_trailers'>
                                      {translate('dealerlocator.checkboxboat')}
                                      ({props.boat_trailerscount})
                                    </label>
                                    <i className="icon-trailerboat" aria-hidden="true"></i>
                                  </li>
                                  <li className="custom-checkbox">
                                    <input 
                                      type="checkbox"
                                      id='service'
                                      onChange={() => onChange("service")}
                                      checked={props.selected.includes("service")}
                                      onKeyDown={(e) => {
                                        if (e.key === ' ' || e.key === 'Enter') {
                                          e.preventDefault();
                                          onChange("service");
                                        }
                                      }}
                                    />
                                    <label htmlFor='service'>
                                      {translate('dealerlocator.offerservice')}
                                      ({props.servicecount})
                                    </label>
                                    <svg aria-hidden="true" focusable="false">
                                      <use href="/ui/images/sprites/brenderup.svg#service"></use>
                                    </svg>
                                    </li>
                                    {props.isClickAndCollect == "True" && 
                                      <li className="custom-checkbox">
                                        <input 
                                          type="checkbox"
                                          id='active'
                                          onChange={() => onChange("active")}
                                          //selected={props.selected.includes("active")}
                                          checked={props.selected.includes("active")} 
                                          onKeyDown={(e) => {
                                            if (e.key === ' ' || e.key === 'Enter') {
                                              e.preventDefault();
                                              onChange("active");
                                            }
                                          }}
                                        /> 
                                        <label htmlFor='active'>
                                          {translate('dealerlocator.clickncollect')}
                                          ({props.activecount ? props.activecount : 0})
                                        </label>
                                        <svg aria-hidden="true" focusable="false">
                                          <use href="/ui/images/sprites/brenderup.svg#fast-cart"></use>
                                        </svg>
                                      </li>
                                    } 
                              </ul>
                          </div>
                      </div>
                  </div>
              </div>
              <div className="dealerLocator__wrap">
                  <div className="dealerLocator__partner-icons">
                      {
                          props.dealerLocatorIcons.premiumTitle && (
                          <div className="dealerLocator__partner-icons--items">
                              <div className="dealerLocator__partner-icons--icon">
                                  <img src={props.dealerLocatorIcons.premium} alt={props.dealerLocatorIcons.premiumTitle} />
                              </div>
                              {props.dealerLocatorIcons.premiumTitle}
                          </div>)
                      }
                      {
                          props.dealerLocatorIcons.partnerTitle && (
                          <div className="dealerLocator__partner-icons--items">
                              <div className="dealerLocator__partner-icons--icon">
                                  <img src={props.dealerLocatorIcons.partner} alt={props.dealerLocatorIcons.partnerTitle} />
                              </div>
                              {props.dealerLocatorIcons.partnerTitle}
                          </div>)
                      }
                      {
                          props.dealerLocatorIcons.authorizedTitle && (
                          <div className="dealerLocator__partner-icons--items">
                              <div className="dealerLocator__partner-icons--icon">
                                  <img src={props.dealerLocatorIcons.authorized} alt={props.dealerLocatorIcons.authorizedTitle} />
                              </div>
                              {props.dealerLocatorIcons.authorizedTitle}
                          </div>
                          )
                      }
                  </div>
                  <div className="dealerLocator__tabs">
                  {/* Tab 1 */}
                  <input
                    className="dealerLocator__tabs--input"
                    type="radio"
                    name="tabs"
                    id="tab1"
                    checked={activeTab === 1}
                    onChange={() => setTab(1, "show-map")}
                    tabIndex="-1"
                  />
                  <label
                    ref={(el) => (tabRefs.current[0] = el)}
                    className={`select-label show-map ${activeTab === 1 ? "active-tab" : ""}`}
                    htmlFor="tab1"
                    tabIndex={0}
                    role="tab"
                    aria-selected={activeTab === 1}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        document.getElementById("tab1")?.click();
                      } else {
                        handleArrowNavigation(e, 0);
                      }
                    }}
                  >
                    {translate("dealerlocator.tabshowonmap")} ({getResellerCount()})
                  </label>

                  {/* Tab 2 */}
                  <input
                    className="dealerLocator__tabs--input"
                    type="radio"
                    name="tabs"
                    id="tab2"
                    checked={activeTab === 2}
                    onChange={() => setTab(2, "closest")}
                    tabIndex="-1"
                  />
                  <label
                    ref={(el) => (tabRefs.current[1] = el)}
                    className={`select-label closest ${activeTab === 2 ? "active-tab" : ""}`}
                    htmlFor="tab2"
                    tabIndex={0}
                    role="tab"
                    aria-selected={activeTab === 2}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        document.getElementById("tab2")?.click();
                      } else {
                        handleArrowNavigation(e, 1);
                      }
                    }}
                  >
                    {translate("dealerlocator.tabclosest")} ({getTotalClosestLocation()})
                  </label>

                  {/* Tab 3 */}
                  <input
                    className="dealerLocator__tabs--input"
                    type="radio"
                    name="tabs"
                    id="tab3"
                    checked={activeTab === 3}
                    onChange={() => setTab(3, "show-list")}
                    tabIndex="-1"
                  />
                  <label
                    ref={(el) => (tabRefs.current[2] = el)}
                    className={`select-label show-list ${activeTab === 3 ? "active-tab" : ""}`}
                    htmlFor="tab3"
                    tabIndex={0}
                    role="tab"
                    aria-selected={activeTab === 3}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        document.getElementById("tab3")?.click();
                      } else {
                        handleArrowNavigation(e, 2);
                      }
                    }}
                  >
                    {translate("dealerlocator.tabshowaslist")} ({getResellerCount()})
                  </label>

                  {/* Flik-innehåll (oförändrat) */}
                  <div className="tab">
                    <div className="content content1">
                      <GoogleMap
                        defaultZoomSize={10}
                        dealers={props}
                        lat={props.firstload == true && selectedPlace ? selectedPlace.lat : props.myLat || props.lat}
                        lng={props.firstload == true && selectedPlace ? selectedPlace.lng : props.myLongt || props.longt}
                      />
                      <GoogleMapHandler />
                    </div>
                    <div className="content content2">
                      <ul className="dealerlist closest">
                        <CloseDealerListDetailTags allreseller={props.allReseller} lat={props.firstload == true && props.myLat != 0 ? props.myLat : props.lat} longt={props.firstload == true && props.myLongt != 0 ? props.myLongt : props.longt} isClickAndCollect={props.isClickAndCollect}/>
                      </ul>
                    </div>
                    <div className="content content3">
                      <ul className="dealerlist">
                        {props.items &&
                          props.items.map((dealer, index) => (
                            <Fragment key={"dealer-list-fg" + index}>
                              <DealerListDetailTags resellername={dealer.resellerAddress[0].shopName} productAssortment={dealer.productAssortment} addresses={dealer.resellerAddress} key={`MAPAddress_${index}`} lat={props.firstload == true && props.myLat != 0 ? props.myLat : props.lat} longt={props.firstload == true && props.myLongt != 0 ? props.myLongt : props.longt} isClickAndCollect={props.isClickAndCollect}/>
                            </Fragment>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
          </APIProvider>
        )
    );
}

const mapStateToProps = state => {
    const { resellermaplist } = state;
    return { ...resellermaplist };
}

const mapDispatchToProps = dispatch => {
    return {
        getAPIData: () => {
            dispatch(loadDealerLocator());
        },
        update: (value) => {
            dispatch(update(value));
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DealerLocationContainer);